import { useCallback, useEffect, useMemo, useState } from "react";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  EXPERIENCE_CATEGORIES,
  EXPERIENCE_STATUS,
  commerceMeta,
  commerceOptions,
  type Experience,
} from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_EXPERIENCE = {
  name: "",
  farm_name: "",
  farmer_name: "",
  category: "Farm Life",
  description: "",
  duration: "",
  price: 0,
  capacity: 10,
  age_range: "",
  audience: "",
  meeting_point: "",
  safety: "",
  status: "draft",
  image: "",
};

export default function AdminExperiences() {
  const [rows, setRows] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_EXPERIENCE });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Experience>("experiences", "name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load experiences.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Experience>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("experiences", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createExperience = async () => {
    if (!draft.name.trim()) {
      setError("An experience name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Experience>("experiences", {
        ...draft,
        name: draft.name.trim(),
        highlights: [],
        featured: false,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_EXPERIENCE });
      notify(`${created.name} added to the catalogue.`);
    } catch {
      setError("We couldn't create that experience. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (categoryFilter !== "all" && row.category !== categoryFilter) return false;
      if (!query) return true;
      return [row.name, row.farm_name || "", row.farmer_name || "", row.category || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, categoryFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<Experience>[] = [
    {
      key: "name",
      header: "Experience",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-md overflow-hidden bg-background-100 shrink-0 flex items-center justify-center text-primary-700">
            {row.image ? (
              <img src={row.image} alt={row.name} className="w-full h-full object-cover object-top" />
            ) : (
              <i className="ri-compass-3-line"></i>
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.farm_name || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
          {row.category || "—"}
        </span>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.duration || "—"}</span>,
    },
    {
      key: "price",
      header: "Price",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
          {formatRupees(row.price)}
        </span>
      ),
    },
    {
      key: "capacity",
      header: "Capacity",
      render: (row) => <span className="text-sm text-foreground-700">{row.capacity}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <StatusPill {...commerceMeta(EXPERIENCE_STATUS, row.status)} />,
    },
    {
      key: "featured",
      header: "Featured",
      render: (row) =>
        row.featured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2 py-1 rounded-full">
            <i className="ri-star-fill"></i> Yes
          </span>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Experiences"
        title="Experiences"
        subtitle="The full activity catalogue — what each farm offers, its pricing, capacity and publication state."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Experience
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total experiences", value: rows.length, icon: "ri-compass-3-line", tone: "primary" },
          {
            label: "Published",
            value: rows.filter((row) => row.status === "published").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Featured",
            value: rows.filter((row) => row.featured).length,
            icon: "ri-star-line",
            tone: "accent",
          },
          {
            label: "Avg. price",
            value: formatRupees(rows.length ? Math.round(rows.reduce((sum, row) => sum + Number(row.price || 0), 0) / rows.length) : 0),
            icon: "ri-price-tag-3-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, farm, farmer…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(EXPERIENCE_STATUS)]}
          />
          <Select
            label="Category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[
              { value: "all", label: "All categories" },
              ...EXPERIENCE_CATEGORIES.map((category) => ({ value: category, label: category })),
            ]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} experiences
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading experiences…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-compass-3-line"
            title={rows.length === 0 ? "No experiences yet" : "No experiences match"}
            message="Experiences published on the website will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Experience"}
        subtitle="Experience detail"
      >
        {selected ? (
          <>
            {selected.image ? (
              <div className="w-full h-40 rounded-lg overflow-hidden bg-background-100">
                <img src={selected.image} alt={selected.name} className="w-full h-full object-cover object-top" />
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...commerceMeta(EXPERIENCE_STATUS, selected.status)} />
              {selected.category ? (
                <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">
                  {selected.category}
                </span>
              ) : null}
              <span className="text-xs font-semibold text-foreground-700 bg-background-100 border border-background-300 px-2.5 py-1 rounded-full">
                {formatRupees(selected.price)}
              </span>
            </div>

            {selected.description ? (
              <p className="text-sm text-foreground-700 leading-relaxed">{selected.description}</p>
            ) : null}

            <DetailList
              items={[
                { icon: "ri-plant-line", label: "Farm", value: selected.farm_name || "—" },
                { icon: "ri-user-star-line", label: "Farmer", value: selected.farmer_name || "—" },
                { icon: "ri-time-line", label: "Duration", value: selected.duration || "—" },
                { icon: "ri-group-line", label: "Capacity", value: `${selected.capacity} guests` },
                { icon: "ri-parent-line", label: "Age range", value: selected.age_range || "—" },
                { icon: "ri-map-pin-line", label: "Meeting point", value: selected.meeting_point || "—" },
                { icon: "ri-shield-check-line", label: "Safety notes", value: selected.safety || "—" },
              ]}
            />

            {selected.highlights && selected.highlights.length > 0 ? (
              <Field label="Highlights">
                <div className="flex flex-wrap gap-2">
                  {selected.highlights.map((item) => (
                    <span
                      key={item}
                      className="text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2.5 py-1 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </Field>
            ) : null}

            {selected.included || selected.excluded ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selected.included ? (
                  <Field label="Included">
                    <p className="text-sm text-foreground-700">{selected.included}</p>
                  </Field>
                ) : null}
                {selected.excluded ? (
                  <Field label="Excluded">
                    <p className="text-sm text-foreground-700">{selected.excluded}</p>
                  </Field>
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Publication status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Experience status updated.")}
                  className={fieldInput}
                >
                  {commerceOptions(EXPERIENCE_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Featured">
                <button
                  type="button"
                  onClick={() => update(selected.id, { featured: !selected.featured }, selected.featured ? "Removed from featured." : "Marked as featured.")}
                  className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                    selected.featured
                      ? "bg-accent-500 text-accent-950 border-accent-500"
                      : "bg-background-100 text-foreground-700 border-background-300"
                  }`}
                >
                  <i className={selected.featured ? "ri-star-fill" : "ri-star-line"}></i>
                  {selected.featured ? "Featured" : "Not featured"}
                </button>
              </Field>
            </div>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a new experience"
        subtitle="New record"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createExperience} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create experience
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Experience name">
            <input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="e.g. Paddy Harvest Walk"
              className={textInput}
            />
          </TextField>
          <TextField label="Category">
            <select
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              className={fieldInput}
            >
              {EXPERIENCE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Farm">
            <input
              value={draft.farm_name}
              onChange={(event) => setDraft({ ...draft, farm_name: event.target.value })}
              placeholder="e.g. Sri Lakshmi Farm"
              className={textInput}
            />
          </TextField>
          <TextField label="Farmer">
            <input
              value={draft.farmer_name}
              onChange={(event) => setDraft({ ...draft, farmer_name: event.target.value })}
              placeholder="e.g. Ramesh Naidu"
              className={textInput}
            />
          </TextField>
          <TextField label="Price (₹)">
            <input
              type="number"
              value={draft.price}
              onChange={(event) => setDraft({ ...draft, price: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Capacity (guests)">
            <input
              type="number"
              value={draft.capacity}
              onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Duration">
            <input
              value={draft.duration}
              onChange={(event) => setDraft({ ...draft, duration: event.target.value })}
              placeholder="e.g. 3 hours"
              className={textInput}
            />
          </TextField>
          <TextField label="Age range">
            <input
              value={draft.age_range}
              onChange={(event) => setDraft({ ...draft, age_range: event.target.value })}
              placeholder="e.g. 5+ years"
              className={textInput}
            />
          </TextField>
          <TextField label="Audience">
            <input
              value={draft.audience}
              onChange={(event) => setDraft({ ...draft, audience: event.target.value })}
              placeholder="e.g. Families, Schools"
              className={textInput}
            />
          </TextField>
          <TextField label="Meeting point">
            <input
              value={draft.meeting_point}
              onChange={(event) => setDraft({ ...draft, meeting_point: event.target.value })}
              placeholder="e.g. Farm entrance gate"
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(EXPERIENCE_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Safety notes">
            <input
              value={draft.safety}
              onChange={(event) => setDraft({ ...draft, safety: event.target.value })}
              placeholder="e.g. Comfortable footwear advised"
              className={textInput}
            />
          </TextField>
          <div className="sm:col-span-2">
            <TextField label="Description">
              <textarea
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                rows={3}
                maxLength={500}
                placeholder="A short summary of the experience…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
        </div>
      </Modal>
      {node}
    </div>
  );
}